import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../contexts/SnackbarContext';
import { DataSource, DocumentInDataSource } from '../models/models';
import { collectionService } from '../services/collection.service';
import { parseError } from '../services/http.service';

interface UseCollectionActionsProps {
  onDataChanged?: () => void;
  onSourceDeleted?: (sourceId: number) => void;
  onDocumentDeleted?: (documentId: number) => void;
  onRefreshSource?: (sourceId: number) => void;
}

interface SourceContextMenuState {
  mouseX: number;
  mouseY: number;
  source: DataSource;
}

interface DocumentContextMenuState {
  mouseX: number;
  mouseY: number;
  document: DocumentInDataSource;
  sourceId: number;
}

interface EditNameDialogState {
  sourceId: number;
  currentName: string;
}

interface AddDocumentDialogState {
  sourceId: string;
}

interface DeleteSourceDialogState {
  source: DataSource;
}

interface DeleteDocumentDialogState {
  document: DocumentInDataSource;
  sourceId: number;
}

export const useCollectionActions = ({
  onDataChanged,
  onSourceDeleted,
  onDocumentDeleted,
  onRefreshSource,
}: UseCollectionActionsProps = {}) => {
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [sourceContextMenu, setSourceContextMenu] = useState<SourceContextMenuState | null>(null);
  const [documentContextMenu, setDocumentContextMenu] = useState<DocumentContextMenuState | null>(null);
  const [editNameDialog, setEditNameDialog] = useState<EditNameDialogState | null>(null);
  const [addDocumentDialog, setAddDocumentDialog] = useState<AddDocumentDialogState | null>(null);
  const [deleteSourceDialog, setDeleteSourceDialog] = useState<DeleteSourceDialogState | null>(null);
  const [deleteDocumentDialog, setDeleteDocumentDialog] = useState<DeleteDocumentDialogState | null>(null);

  const openSourceContextMenu = useCallback((event: React.MouseEvent, source: DataSource) => {
    event.preventDefault();
    event.stopPropagation();
    setSourceContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      source,
    });
  }, []);

  const closeSourceContextMenu = useCallback(() => {
    setSourceContextMenu(null);
  }, []);

  const openDocumentContextMenu = useCallback((event: React.MouseEvent, document: DocumentInDataSource, sourceId: number) => {
    event.preventDefault();
    event.stopPropagation();
    setDocumentContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
      document,
      sourceId,
    });
  }, []);

  const closeDocumentContextMenu = useCallback(() => {
    setDocumentContextMenu(null);
  }, []);

  const openEditNameDialog = useCallback((source: DataSource) => {
    setEditNameDialog({
      sourceId: source.id,
      currentName: source.name,
    });
    closeSourceContextMenu();
  }, [closeSourceContextMenu]);

  const closeEditNameDialog = useCallback(() => {
    setEditNameDialog(null);
  }, []);

  const openAddDocumentDialog = useCallback((source: DataSource) => {
    setAddDocumentDialog({
      sourceId: String(source.id),
    });
    closeSourceContextMenu();
  }, [closeSourceContextMenu]);

  const closeAddDocumentDialog = useCallback(() => {
    setAddDocumentDialog(null);
  }, []);

  const openDeleteSourceDialog = useCallback((source: DataSource) => {
    setDeleteSourceDialog({
      source,
    });
    closeSourceContextMenu();
  }, [closeSourceContextMenu]);

  const closeDeleteSourceDialog = useCallback(() => {
    setDeleteSourceDialog(null);
  }, []);

  const openDeleteDocumentDialog = useCallback((document: DocumentInDataSource, sourceId: number) => {
    setDeleteDocumentDialog({
      document,
      sourceId,
    });
    closeDocumentContextMenu();
  }, [closeDocumentContextMenu]);

  const closeDeleteDocumentDialog = useCallback(() => {
    setDeleteDocumentDialog(null);
  }, []);

  const updateSourceName = useCallback(async (newName: string) => {
    if (!editNameDialog) return;

    try {
      await collectionService.updateSourceName(editNameDialog.sourceId, newName);
      onDataChanged?.();
      showSnackbar(t('fileSelection.nameUpdated'), 'success');
    } catch (error) {
      showSnackbar(parseError(error), 'error');
    } finally {
      closeEditNameDialog();
    }
  }, [editNameDialog, onDataChanged, closeEditNameDialog, showSnackbar, t]);

  const deleteSource = useCallback(async () => {
    if (!deleteSourceDialog) return;

    try {
      await collectionService.deleteSource(deleteSourceDialog.source.id);
      onSourceDeleted?.(deleteSourceDialog.source.id);
      onDataChanged?.();
      showSnackbar(t('fileSelection.sourceDeleted'), 'success');
    } catch (error) {
      showSnackbar(parseError(error), 'error');
    } finally {
      closeDeleteSourceDialog();
    }
  }, [deleteSourceDialog, onSourceDeleted, onDataChanged, closeDeleteSourceDialog, showSnackbar, t]);

  const addDocuments = useCallback(async () => {
    if (!addDocumentDialog) return;

    collectionService.invalidateCache();
    onDataChanged?.();
    closeAddDocumentDialog();
  }, [addDocumentDialog, onDataChanged, closeAddDocumentDialog]);

  const deleteDocument = useCallback(async () => {
    if (!deleteDocumentDialog) return;

    try {
      await collectionService.deleteDocument(deleteDocumentDialog.document.id);
      onDocumentDeleted?.(deleteDocumentDialog.document.id);
      onRefreshSource?.(deleteDocumentDialog.sourceId);
      onDataChanged?.();
      showSnackbar(t('fileSelection.documentDeleted'), 'success');
    } catch (error) {
      showSnackbar(parseError(error), 'error');
    } finally {
      closeDeleteDocumentDialog();
    }
  }, [deleteDocumentDialog, onDocumentDeleted, onRefreshSource, onDataChanged, closeDeleteDocumentDialog, showSnackbar, t]);

  return {
    sourceContextMenu,
    documentContextMenu,
    editNameDialog,
    addDocumentDialog,
    deleteSourceDialog,
    deleteDocumentDialog,
    openSourceContextMenu,
    closeSourceContextMenu,
    openDocumentContextMenu,
    closeDocumentContextMenu,
    openEditNameDialog,
    closeEditNameDialog,
    openAddDocumentDialog,
    closeAddDocumentDialog,
    openDeleteSourceDialog,
    closeDeleteSourceDialog,
    openDeleteDocumentDialog,
    closeDeleteDocumentDialog,
    updateSourceName,
    deleteSource,
    addDocuments,
    deleteDocument,
  };
};
